import { type FC, type ReactNode, useState, useRef, useEffect } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, LockKeyhole, RefreshCw, X } from "lucide-react";
import Lottie from "react-lottie";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";

import { Dialog, DialogContent, DialogTitle } from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { IconButton } from "@/shared/components/ui/icon-button";
import { ModalStepLayout } from "@/shared/components/ui/ModalStepLayout";
import { IllustratedDialog } from "@/shared/components/ui/IllustratedDialog";
import { FileCard } from "@/shared/components/ui/file-card";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { FieldError } from "@/shared/components/ui/FieldError";
import { SignatureStepLayout } from "@/shared/components/SignatureStepLayout";
import { TriageModalQrStep } from "@/pages/postloss/overview/components/TriageModalQrStep";
import TPADocumentViewer from "@/shared/components/document-viewer/TPADocumentViewer";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { useUserSettingsData } from "@/shared/hooks/useUserSettingsData";
import { useIdVerificationStatus } from "@/shared/hooks/useIdVerificationStatus";
import { uploadDocument, DocumentLimitError } from "@/shared/utils/uploadDocument";
import { deleteDocument } from "@/shared/utils/deleteDocument";
import { useQuickIdCheck } from "@/shared/hooks/useQuickIdCheck";
import { supabase } from "@/shared/lib/supabase";
import { sentryUtils } from "@/shared/utils/sentry";

import logoIcon from "@/shared/assets/logo/logo-icon.svg";
import irsLogo from "@/shared/assets/illustrations/irs-logo.png";
import equifaxLogo from "@/shared/assets/illustrations/equifax-logo.png";
import scanningAnimation from "@/shared/assets/animations/id-scanning.json";
import verifyAndSignImg from "@/shared/assets/illustrations/verify-and-sign.svg";

import { IdVerifiedScreen } from "@/pages/postloss/find-assets/components/IdVerifiedScreen";
import { UploadMethodCards } from "@/pages/postloss/find-assets/components/UploadMethodCards";

type SignatureType = "tpa" | "irs" | "equifax";

interface SignatureStepConfig {
  signatureType: SignatureType;
  header: ReactNode;
  title: string;
  body: (ctx: { firstName: string; onViewDocument: () => void }) => ReactNode;
  hasEsignConsent?: boolean;
  hidden?: boolean;
  documentUrl?: string;
}

const TPAHeader: FC = () => (
  <div className="relative h-16 w-[120px] shrink-0">
    <div className="absolute left-7 top-0 flex h-16 w-16 items-center justify-center rounded-full border-[1.778px] border-primary">
      <RefreshCw className="h-6 w-6 text-primary" />
    </div>
    <img src={logoIcon} alt="Elayne" className="absolute left-0 top-3 h-10 w-10 rounded-full" />
    <div className="absolute left-20 top-3 flex h-10 w-10 items-center justify-center rounded-full border-2 border-primary bg-background">
      <LockKeyhole className="h-5 w-5 text-secondary" />
    </div>
  </div>
);

const ALL_SIGNATURE_STEPS: SignatureStepConfig[] = [
  {
    signatureType: "tpa",
    header: <TPAHeader />,
    title: "Let Elayne take work off your plate",
    hasEsignConsent: true,
    body: ({ firstName, onViewDocument }) => (
      <>
        <p className="text-sm text-secondary">
          This signature lets Elayne request records for {firstName}'s estate, so the calls, forms,
          and follow-ups with each institution happen for you. Review the{" "}
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-sm text-secondary underline"
            onClick={onViewDocument}
          >
            Elayne Third-Party Authorization Agreement
          </Button>{" "}
          before signing.
        </p>
        <p className="text-sm text-secondary">
          Typing your name signs the agreement and confirms you've read it.
        </p>
      </>
    ),
  },
  {
    signatureType: "irs",
    header: <img src={irsLogo} alt="IRS" className="h-10 w-auto shrink-0" />,
    title: "Permission to request {firstName}'s tax records",
    hasEsignConsent: true,
    documentUrl: "/irs-form-8821.pdf",
    body: ({ firstName, onViewDocument }) => (
      <>
        <p className="text-sm text-secondary">
          The IRS holds records of {firstName}'s accounts, but only releases them to credentialed
          requesters. Elayne is one. This signature lets Elayne request {firstName}'s records
          directly.
        </p>
        <p className="text-sm text-secondary">
          Review the{" "}
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-sm text-secondary underline"
            onClick={onViewDocument}
          >
            IRS authorization packet
          </Button>{" "}
          before signing. It has both forms: the records request (Form 8821) and notice that you act
          for the estate (Form 56).
        </p>
        <p className="text-sm text-secondary">
          Typing your name signs both forms, confirms you've read them, and confirms you have legal
          authority to act for the estate.
        </p>
      </>
    ),
  },
  {
    signatureType: "equifax",
    header: <img src={equifaxLogo} alt="Equifax" className="h-6 w-auto shrink-0" />,
    title: "Permission to request {firstName}'s credit records",
    hidden: true,
    body: ({ firstName, onViewDocument }) => (
      <>
        <div className="text-sm text-secondary">
          <p>One signature, two protections for the estate:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>
              <span className="font-bold">Find accounts and debts.</span> {firstName}'s credit
              report lists what's open in {firstName}'s name, from cards to loans
            </li>
            <li>
              <span className="font-bold">Protect {firstName}'s identity:</span> Identity thieves
              target people who have passed. New activity on the report gets flagged through
              Identity Protection
            </li>
          </ul>
        </div>
        <p className="text-sm text-secondary">
          This signature directs Elayne, as your agent, to request {firstName}'s report from Equifax
          and watch it. Typing your name signs the authorization and confirms you're acting as
          executor of {firstName}'s estate.
        </p>
        <p className="text-sm text-secondary">
          Review the{" "}
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-sm text-secondary underline"
            onClick={onViewDocument}
          >
            Equifax authorization
          </Button>{" "}
          before signing.
        </p>
      </>
    ),
  },
];

const ACTIVE_STEPS = ALL_SIGNATURE_STEPS.filter((s) => !s.hidden);

type IdStep = "verify-id" | "verify-id-qr" | "verify-id-processing" | "verify-id-success";
type ModalPhase = "intro" | "id-verification" | "signatures" | "success";

interface StagedFile {
  file: File;
  documentId?: number;
}

interface TPASignatureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  estateId: number | undefined;
  onSigned: () => void | Promise<void>;
}

export const TPASignatureModal: FC<TPASignatureModalProps> = ({
  open,
  onOpenChange,
  estateId,
  onSigned,
}) => {
  const { authUser, user } = useAppContext();
  const deceasedUserData = useDeceasedUserData();
  const { tpaSignatures, completedAuthorization } = useUserSettingsData();
  const { idCheckPassed } = useIdVerificationStatus();
  const queryClient = useQueryClient();

  const firstName = deceasedUserData?.personData?.first_name || "your loved one";
  const deceasedFullName = deceasedUserData.fullName || "your loved one";

  const { data: existingId, isError: existingIdError } = useQuery({
    queryKey: ["existingIdDocument", estateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estate_documents")
        .select("id, file_name")
        .eq("estate_id", estateId!)
        .eq("document_type", "identification_card")
        .order("created_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      return data?.[0] ?? null;
    },
    enabled: !!estateId && open,
  });

  const hasExistingId = !!existingId;

  const computeInitialPhase = (): ModalPhase => {
    if (completedAuthorization) return "success";
    if (hasExistingId && idCheckPassed) return "signatures";
    if (hasExistingId) return "id-verification";
    return "intro";
  };

  const computeInitialSigIndex = () => {
    if (!tpaSignatures) return 0;
    for (let i = 0; i < ACTIVE_STEPS.length; i++) {
      if (!tpaSignatures[ACTIVE_STEPS[i].signatureType]) return i;
    }
    return ACTIVE_STEPS.length - 1;
  };

  const [phase, setPhase] = useState<ModalPhase>(computeInitialPhase);
  const [idStep, setIdStep] = useState<IdStep>("verify-id");
  const [idFile, setIdFile] = useState<StagedFile | null>(null);
  const [idError, setIdError] = useState<string>();
  const [isIdUploading, setIsIdUploading] = useState(false);
  const [idVerified, setIdVerified] = useState(false);
  const { runCheck: runIdCheck, reset: resetIdCheck } = useQuickIdCheck(estateId);

  const [sigIndex, setSigIndex] = useState(computeInitialSigIndex);
  const [fullName, setFullName] = useState("");
  const [esignConsent, setEsignConsent] = useState(false);
  const [sigErrors, setSigErrors] = useState<{ fullName?: string; consent?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTPADocument, setShowTPADocument] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const currentStep = ACTIVE_STEPS[sigIndex];
  const totalSigSteps = ACTIVE_STEPS.length;
  const isLastSigStep = sigIndex === totalSigSteps - 1;

  const allStepsSigned = tpaSignatures
    ? ACTIVE_STEPS.every((s) => !!tpaSignatures[s.signatureType])
    : false;

  const onSignedPending = useRef(false);

  useEffect(() => {
    if (!open) return;
    if (allStepsSigned && !onSignedPending.current) {
      onSignedPending.current = true;
      Promise.resolve(onSigned())
        .then(() => setPhase("success"))
        .catch(() => onOpenChange(false))
        .finally(() => {
          onSignedPending.current = false;
        });
    } else if (hasExistingId && idCheckPassed) {
      setIdVerified(true);
      if (phase === "intro" || phase === "id-verification") {
        setPhase("signatures");
      }
    }
    if (tpaSignatures) {
      for (let i = 0; i < ACTIVE_STEPS.length; i++) {
        if (!tpaSignatures[ACTIVE_STEPS[i].signatureType]) {
          setSigIndex(i);
          return;
        }
      }
    }
  }, [open, allStepsSigned, hasExistingId, idCheckPassed, tpaSignatures]);

  useEffect(() => {
    if (!open) {
      setIdFile(null);
      setIdError(undefined);
      setIsIdUploading(false);
      setIdStep("verify-id");
      setSigErrors({});
      setSigIndex(computeInitialSigIndex());
      setPhase(computeInitialPhase());
      setIdVerified(hasExistingId && idCheckPassed);
      resetIdCheck();
    }
  }, [open]);

  const handleRemoveId = async () => {
    const docId = idFile?.documentId ?? existingId?.id;
    try {
      if (docId && estateId) {
        await deleteDocument(estateId, docId);
        queryClient.invalidateQueries({ queryKey: ["existingIdDocument", estateId] });
      }
    } catch {
      enqueueSnackbar("Couldn't remove document. Please try again.", { variant: "error" });
    } finally {
      setIdFile(null);
      setIdVerified(false);
      queryClient.invalidateQueries({ queryKey: ["idVerificationStatus"] });
    }
  };

  const handleIdContinue = async () => {
    if (idVerified) {
      setPhase("signatures");
    } else if (!idFile && existingId) {
      setIdStep("verify-id-processing");
    } else if (!idFile) {
      setIdError("Upload your ID to continue");
    } else if (idFile.documentId) {
      setIdStep("verify-id-processing");
    } else if (estateId) {
      setIsIdUploading(true);
      setIdError(undefined);
      try {
        const docId = await uploadDocument(idFile.file, "identification_card", estateId);
        setIdFile((prev) => (prev ? { ...prev, documentId: docId } : prev));
        setIdStep("verify-id-processing");
      } catch (err) {
        setIdError(
          err instanceof DocumentLimitError ? err.message : "Upload failed. Please try again.",
        );
      } finally {
        setIsIdUploading(false);
      }
    }
  };

  useEffect(() => {
    if (phase !== "id-verification" || idStep !== "verify-id-processing") return;

    const documentId = idFile?.documentId ?? existingId?.id;
    if (!estateId) return;
    if (!documentId) {
      if (existingId === null || existingIdError) {
        enqueueSnackbar("Couldn't find your ID document. Please upload again.", {
          variant: "error",
        });
        setIdStep("verify-id");
      }
      return;
    }

    runIdCheck(documentId, {
      onVerified: () => {
        setIdVerified(true);
        setIdStep("verify-id-success");
      },
      onSkipped: () => {
        setIdVerified(true);
        setPhase("signatures");
      },
      onRejected: () => {
        setIdFile(null);
        setIdStep("verify-id");
      },
      onFailed: () => {
        setIdFile(null);
        setIdStep("verify-id");
      },
    });
  }, [phase, idStep, idFile, existingId, existingIdError, estateId, runIdCheck]);

  const validateName = (name: string) => {
    const words = name
      .trim()
      .split(/\s+/)
      .filter((w) => w.length > 0);
    return words.length >= 2;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFullName(e.target.value);
    if (sigErrors.fullName) {
      setSigErrors((prev) => ({ ...prev, fullName: undefined }));
    }
  };

  const captureScreenshot = async (): Promise<string | null> => {
    try {
      const html2canvas = (await import("html2canvas")).default;
      const el = modalRef.current;
      if (!el) return null;
      await new Promise((r) => setTimeout(r, 100));
      await new Promise((r) => requestAnimationFrame(() => r(undefined)));
      await new Promise((r) => setTimeout(r, 100));
      const canvas = await html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: el.scrollWidth,
        height: el.scrollHeight,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
      });
      return canvas.toDataURL("image/png");
    } catch (err) {
      sentryUtils.captureError(
        err,
        "Failed to capture TPA modal screenshot",
        {
          userId: user?.user_id,
          estateId: deceasedUserData?.personData?.estate_id,
        },
        "warning",
      );
      return null;
    }
  };

  const handleSignatureSubmit = async () => {
    const newErrors: { fullName?: string; consent?: string } = {};
    if (!fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (!validateName(fullName)) {
      newErrors.fullName = "Please enter at least first and last name";
    }
    if (currentStep.hasEsignConsent && !esignConsent) {
      newErrors.consent = "You must agree to sign electronically";
    }
    setSigErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const modalScreenshot = await captureScreenshot();
      const { error } = await supabase.functions.invoke("legal/tpa/sign", {
        body: {
          userEmail: authUser?.email || user?.email,
          userName: fullName,
          deceasedName: deceasedFullName,
          consentGiven: true,
          esignConsent,
          signatureType: currentStep.signatureType,
          signatureStep: sigIndex + 1,
          totalSteps: totalSigSteps,
          timestamp: new Date().toISOString(),
          processInBackground: true,
          estateId: estateId || undefined,
          modalScreenshot,
        },
      });
      if (error) throw new Error(error.message || "Failed to process authorization");

      queryClient.invalidateQueries({ queryKey: ["userSettingsSupabase"] });

      if (isLastSigStep) {
        queryClient.invalidateQueries({ queryKey: ["appContext"] });
        queryClient.invalidateQueries({ queryKey: ["documents"] });
        onSignedPending.current = true;
        try {
          await onSigned();
          setPhase("success");
        } catch {
          onOpenChange(false);
        } finally {
          onSignedPending.current = false;
        }
      } else {
        setSigIndex(sigIndex + 1);
        setFullName("");
        setEsignConsent(false);
        setSigErrors({});
      }
    } catch (err) {
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
      sentryUtils.captureError(err, "TPA modal submission failed", {
        userId: user?.user_id,
        estateId: deceasedUserData?.personData?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessCta = () => {
    onOpenChange(false);
  };

  if (phase === "success") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          size="lg"
          className="!p-0 !gap-0 overflow-hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogTitle className="sr-only">Search started</DialogTitle>
          <div className="flex flex-col items-center gap-8 p-8">
            <IconButton
              onClick={handleSuccessCta}
              className="absolute right-6 top-6"
              label="Close"
              tooltip={false}
            >
              <X />
            </IconButton>
            <div className="flex flex-col items-center gap-3 w-[400px]">
              <div className="flex items-center justify-center rounded-full bg-brand p-4">
                <Check className="h-8 w-8 text-white" strokeWidth={3} />
              </div>
              <h4 className="text-center">The search for {firstName}'s assets is on</h4>
              <div className="text-sm text-secondary text-center">
                <p>
                  Right now, requests are going out to the IRS, Equifax, and 100+ financial sources.
                  Nothing is needed from you: Elayne follows up with each source until it answers.
                </p>
                <p className="mt-3">You'll get a notification and email as findings come in.</p>
              </div>
            </div>
            <Button
              variant="brand"
              size="md"
              rounded
              className="w-[400px] px-6 py-2"
              onClick={handleSuccessCta}
            >
              See what's being searched
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (phase === "intro") {
    return (
      <IllustratedDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Verify and sign to start the search"
        description={`Before we can request records for ${firstName}'s estate, we need to confirm who you are and get your permission.`}
        illustration={verifyAndSignImg}
        illustrationAlt="Verify and sign"
        ctaText="Verify and sign"
        onCtaClick={() => setPhase("id-verification")}
        dismissable={false}
      >
        <div className="flex flex-col gap-4">
          <ol className="flex flex-col gap-3">
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-subtle text-xs">
                1
              </span>
              <p className="text-sm text-secondary">Verify your identity with a photo ID</p>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-subtle text-xs">
                2
              </span>
              <p className="text-sm text-secondary">
                Sign the authorization so Elayne can request records for you
              </p>
            </li>
          </ol>
          <p className="text-sm text-secondary">
            Then we'll get to work uncovering accounts, property, insurance, and debts in{" "}
            {firstName}'s name.
          </p>
        </div>
      </IllustratedDialog>
    );
  }

  if (phase === "id-verification") {
    return (
      <>
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent
            size="flow"
            className="!p-0 !gap-0 overflow-hidden"
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <DialogTitle className="sr-only">Verify your ID</DialogTitle>

            <IconButton
              onClick={() => onOpenChange(false)}
              className="absolute right-4 top-4 z-10"
              label="Close"
              tooltip={false}
            >
              <X />
            </IconButton>

            {idStep === "verify-id" && (
              <ModalStepLayout
                title="Verify your ID"
                description="This confirms you're the person named in the letters, so no one else can act on the estate."
                onBack={() => setPhase("intro")}
                actionButton={
                  <Button
                    variant="brand"
                    size="md"
                    onClick={handleIdContinue}
                    disabled={isIdUploading}
                  >
                    <LockKeyhole className="h-4 w-4" />
                    {isIdUploading ? "Uploading..." : "Continue"}
                  </Button>
                }
              >
                {!(idFile || existingId) && (
                  <UploadMethodCards
                    variant="side-by-side"
                    onFileSelected={(file) => {
                      setIdFile({ file });
                      setIdError(undefined);
                    }}
                    onSelectPhone={() => setIdStep("verify-id-qr")}
                  />
                )}
                {(idFile || existingId) && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-primary">1 document added</p>
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-brand">
                        <Check className="h-3 w-3 text-white" strokeWidth={3} />
                      </div>
                    </div>
                    <FileCard
                      fileName={idFile?.file.name ?? existingId?.file_name ?? "ID document"}
                      fileSize={idFile?.file.size}
                      onRemove={handleRemoveId}
                      status={idVerified ? "complete" : "idle"}
                    />
                  </div>
                )}
                {idError && <p className="text-sm text-error">{idError}</p>}
              </ModalStepLayout>
            )}

            {idStep === "verify-id-qr" && (
              <TriageModalQrStep
                title="Verify your ID"
                description="Take a photo of your government-issued ID with your phone."
                documentType="identification_card"
                onBack={() => setIdStep("verify-id")}
                onFileUploaded={async () => {
                  await queryClient.invalidateQueries({
                    queryKey: ["existingIdDocument", estateId],
                  });
                  setIdStep("verify-id-processing");
                }}
              />
            )}

            {idStep === "verify-id-processing" && (
              <div className="flex h-full items-center justify-center">
                <div className="flex flex-col items-center justify-center gap-6">
                  <div className="w-80">
                    <Lottie
                      options={{
                        loop: true,
                        autoplay: true,
                        animationData: scanningAnimation,
                        rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
                      }}
                      isClickToPauseDisabled
                    />
                  </div>
                  <div className="flex flex-col items-center gap-2 text-center">
                    <h3>Verifying your ID</h3>
                    <p className="text-secondary">This usually takes about 20 seconds</p>
                  </div>
                </div>
              </div>
            )}

            {idStep === "verify-id-success" && (
              <div className="flex h-full items-center justify-center">
                <IdVerifiedScreen onComplete={() => setPhase("signatures")} />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // phase === "signatures"
  const stepTitle = currentStep.title.replace("{firstName}", firstName);

  const sigBody = (
    <>
      {currentStep.body({ firstName, onViewDocument: () => setShowTPADocument(true) })}
      {currentStep.hasEsignConsent && (
        <>
          <label className="flex items-start gap-2 text-sm text-secondary cursor-pointer">
            <Checkbox
              checked={esignConsent}
              onCheckedChange={(checked) => {
                setEsignConsent(checked === true);
                if (sigErrors.consent) {
                  setSigErrors((prev) => ({ ...prev, consent: undefined }));
                }
              }}
              className="mt-0.5"
            />
            <span>I agree to sign and receive documents electronically.</span>
          </label>
          {sigErrors.consent && <FieldError message={sigErrors.consent} />}
        </>
      )}
    </>
  );

  return (
    <>
      <SignatureStepLayout
        open={open}
        onOpenChange={onOpenChange}
        modalRef={modalRef}
        header={currentStep.header}
        stepLabel={`Signature ${sigIndex + 1} of ${totalSigSteps}`}
        title={stepTitle}
        body={sigBody}
        fullName={fullName}
        onNameChange={handleNameChange}
        nameError={sigErrors.fullName}
        isSubmitting={isSubmitting}
        isLastStep={isLastSigStep}
        onSubmit={handleSignatureSubmit}
        onClose={() => onOpenChange(false)}
      />

      <TPADocumentViewer
        isOpen={showTPADocument}
        onClose={() => setShowTPADocument(false)}
        pdfUrl={currentStep.documentUrl}
      />
    </>
  );
};
